// sections/PairWithSection.js
import React, { useState } from 'react';
import { getMethod } from '../../../../utils/api-service/api-service';
import { toast } from 'react-toastify';

const PairWithSection = ({ pairedProducts, onPairedProductsUpdate }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [loadingSearch, setLoadingSearch] = useState(false);

    // Search for products
    const handleProductSearch = async (query) => {
        setSearchQuery(query);
        if (query.length < 2) {
            setSearchResults([]);
            return;
        }

        setLoadingSearch(true);
        try {
            const response = await getMethod(`/products/search?q=${encodeURIComponent(query)}`);
            if (response.success) {
                // Filter out products already added
                const existingProductIds = pairedProducts.map(pp => pp.product?._id || pp.product);
                const filteredResults = response.products.filter(
                    product => !existingProductIds.includes(product._id)
                );
                setSearchResults(filteredResults);
            }
        } catch (error) {
            toast.error('Error searching products');
        } finally {
            setLoadingSearch(false);
        }
    };

    // Add paired product
    const addPairedProduct = (product) => {
        const newPairedProduct = {
            product: product,
            priority: pairedProducts.length,
            colorway: null
        };
        const updatedPairedProducts = [...pairedProducts, newPairedProduct];
        onPairedProductsUpdate(updatedPairedProducts);
        setSearchQuery('');
        setSearchResults([]);
    };

    // Remove paired product
    const removePairedProduct = (index) => {
        const updatedPairedProducts = pairedProducts.filter((_, i) => i !== index);
        // Recalculate priorities
        const reorderedProducts = updatedPairedProducts.map((pp, idx) => ({
            ...pp,
            priority: idx
        }));
        onPairedProductsUpdate(reorderedProducts);
    };

    // Update priority
    const updatePriority = (index, newPriority) => {
        const updatedPairedProducts = [...pairedProducts];
        updatedPairedProducts[index].priority = parseInt(newPriority);
        onPairedProductsUpdate(updatedPairedProducts);
    };

    // Update colorway
    const updateColorway = async (index, productId) => {
        // Fetch product colorways
        try {
            const response = await getMethod(`/products/${productId}/colorways`);
            if (response.success) {
                // Show colorway selector
                // This would ideally be a modal or dropdown
                // For now, just set first colorway
                const updatedPairedProducts = [...pairedProducts];
                if (response.colorways.length > 0) {
                    updatedPairedProducts[index].colorway = response.colorways[0]._id;
                }
                onPairedProductsUpdate(updatedPairedProducts);
            }
        } catch (error) {
            toast.error('Error fetching product colorways');
        }
    };

    return (
        <div>
            <h5 className="border-bottom pb-2 mb-3">
                <i className="bi bi-link-45deg me-2"></i>
                Pair It With
            </h5>

            {/* Search and Add Product */}
            <div className="mb-4">
                <label className="form-label">Search and Add Related Product</label>
                <div className="input-group">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Search products by name or code..."
                        value={searchQuery}
                        onChange={(e) => handleProductSearch(e.target.value)}
                    />
                    {loadingSearch && (
                        <span className="input-group-text">
                            <div className="spinner-border spinner-border-sm" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </span>
                    )}
                </div>

                {/* Search Results */}
                {searchResults.length > 0 && (
                    <div className="mt-2 border rounded p-2 bg-light">
                        <small className="text-muted">Search results:</small>
                        <div className="mt-2">
                            {searchResults.map(product => (
                                <div
                                    key={product._id}
                                    className="d-flex justify-content-between align-items-center p-2 border-bottom"
                                >
                                    <div>
                                        <strong>{product.name}</strong>
                                        <div>
                                            <small className="text-muted">
                                                Code: {product.code} | Category: {product.category?.name}
                                            </small>
                                        </div>
                                    </div>
                                    <button
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => addPairedProduct(product)}
                                    >
                                        Add
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Paired Products List */}
            <div className="mb-3">
                {pairedProducts.length === 0 ? (
                    <div className="alert alert-info">
                        No paired products added. You can add products that go well with this product.
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-sm">
                            <thead className="table-light">
                                <tr>
                                    <th>Product</th>
                                    <th>Code</th>
                                    <th>Priority</th>
                                    <th>Colorway</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pairedProducts.map((paired, index) => (
                                    <tr key={index}>
                                        <td>
                                            <strong>{paired.product?.name}</strong>
                                        </td>
                                        <td>{paired.product?.code}</td>
                                        <td>
                                            <input
                                                type="number"
                                                className="form-control form-control-sm"
                                                style={{ width: '80px' }}
                                                value={paired.priority}
                                                min="0"
                                                onChange={(e) => updatePriority(index, e.target.value)}
                                            />
                                        </td>
                                        <td>
                                            {paired.colorway ? (
                                                <span className="badge bg-success">Selected</span>
                                            ) : (
                                                <button
                                                    className="btn btn-sm btn-outline-secondary"
                                                    onClick={() => updateColorway(index, paired.product?._id)}
                                                >
                                                    Select Colorway
                                                </button>
                                            )}
                                        </td>
                                        <td>
                                            <button
                                                className="btn btn-sm btn-danger"
                                                onClick={() => removePairedProduct(index)}
                                            >
                                                <i className="bi bi-trash"></i>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PairWithSection;